import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

const STORAGE_KEY = "denker.compact-agent-display";
const AUTO_PEEK_STORAGE_KEY = "denker.compact-agent-auto-peek";

type CompactAgentDisplayMode = "cursor" | "dock";

type CompactAgentAvatarAnchor = {
  agentId: string;
  displayId: number | null;
  x: number;
  y: number;
  updatedAt: number;
};

type CompactAgentDisplayState = {
  mode: CompactAgentDisplayMode;
  dockEnabled: boolean;
  autoPeekEnabled: boolean;
  firstAvatarAnchor: CompactAgentAvatarAnchor | null;
  highlightedAgentId: string | null;
  setDockEnabled: (enabled: boolean) => void;
  setAutoPeekEnabled: (enabled: boolean) => void;
  setFirstAvatarAnchor: (anchor: CompactAgentAvatarAnchor | null) => void;
  setHighlightedAgentId: (agentId: string | null) => void;
  toggleDockEnabled: () => void;
  toggleAutoPeekEnabled: () => void;
};

function loadInitialMode(): CompactAgentDisplayMode {
  if (typeof window === "undefined") return "cursor";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dock"
      ? "dock"
      : "cursor";
  } catch {
    return "cursor";
  }
}

function persist(mode: CompactAgentDisplayMode) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Persistence is best-effort; the in-memory mirrored setting still works.
  }
}

function loadInitialAutoPeekEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(AUTO_PEEK_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistAutoPeek(enabled: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AUTO_PEEK_STORAGE_KEY, String(enabled));
  } catch {
    // Persistence is best-effort; the in-memory mirrored setting still works.
  }
}

export const useCompactAgentDisplayStore = create<CompactAgentDisplayState>()(
  mirroredStore("compactAgentDisplay", (set, get) => {
    const initialMode = loadInitialMode();
    return {
      mode: initialMode,
      dockEnabled: initialMode === "dock",
      autoPeekEnabled: loadInitialAutoPeekEnabled(),
      firstAvatarAnchor: null,
      highlightedAgentId: null,
      setDockEnabled: (enabled) => {
        const mode: CompactAgentDisplayMode = enabled ? "dock" : "cursor";
        set({ mode, dockEnabled: enabled });
        persist(mode);
      },
      setAutoPeekEnabled: (enabled) => {
        set({ autoPeekEnabled: enabled });
        persistAutoPeek(enabled);
      },
      setFirstAvatarAnchor: (anchor) => set({ firstAvatarAnchor: anchor }),
      setHighlightedAgentId: (agentId) => set({ highlightedAgentId: agentId }),
      toggleDockEnabled: () => {
        get().setDockEnabled(!get().dockEnabled);
      },
      toggleAutoPeekEnabled: () => {
        get().setAutoPeekEnabled(!get().autoPeekEnabled);
      },
    };
  }),
);

export type { CompactAgentAvatarAnchor, CompactAgentDisplayMode };
